import React, { useState, useRef, useEffect } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

interface ImagePair {
    left_id: number;
    right_id: number;
    leftImage: string;
    rightImage: string;
}

const HomeItems: Record<string, string[]> = {
    "₹1": ["🍎", "🍌", "🍇", "🍊", "🍓", "🍉", "🍍", "🥭", "🍒", "🍑", "🥝", "🍐", "🍋", "🍈", "🍏"],
    "₹2": ["🥕", "🥦", "🌽", "🍅", "🥒", "🧅", "🫑", "🥬", "🍆", "🫘"],
    "₹5": ["🎲", "🪀", "🧸", "🚂", "🪁", "🎯", "🎳", "🛴", "🏎️", "🎠"],
    "₹10": ["🍔", "🍕", "🍫", "🥤", "🍰"],
    "₹20": ["👕", "👖", "👟", "👜", "💍"],
    "₹50": ["🛏️", "🛋️", "🚪", "🖼️", "🪑", "🪞", "🏮"],
    "₹100": ["📱", "💻", "🎧", "📺", "⌚"]
};


const MatchMoney: React.FC = () => {
    const [connections, setConnections] = useState<{ x1: number; y1: number; x2: number; y2: number; correct: boolean }[]>([]);
    const [selectedLeft, setSelectedLeft] = useState<{ id: number; x: number; y: number } | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [questions, setQuestions] = useState<ImagePair[]>([]);
    const [height, setHeight] = useState('0px');
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    // Get element position relative to .match-container
    const getRelativePosition = (element: HTMLDivElement) => {
        const container = containerRef.current;
        if (!container) return { x: 0, y: 0 };

        const elementRect = element.getBoundingClientRect(); setHeight((container.scrollHeight) + 'px')
        const containerRect = container.getBoundingClientRect();

        const screenWidth = window.innerWidth;
        let elementX, elementY;
        if (screenWidth >= 1024) {
            elementX = element.offsetLeft + elementRect.width;
            elementY = element.offsetTop + elementRect.height / 2;
        }
        else {
            elementX = element.offsetLeft + elementRect.width / 2;
            elementY = element.offsetTop + elementRect.height / 2;
        }

        return {
            x: elementX,
            y: elementY
        }
    };

    // Handle left image click
    const handleLeftClick = (event: React.MouseEvent<HTMLDivElement>, id: number) => {
        const position = getRelativePosition(event.currentTarget);
        setSelectedLeft({ id, x: position.x, y: position.y });
    };

    // Handle right image click and draw SVG line
    const handleRightClick = (event: React.MouseEvent<HTMLDivElement>, rightId: number) => {
        if (selectedLeft) {
            const position = getRelativePosition(event.currentTarget);

            // Check if the connection is correct
            const isCorrect = questions.some(pair => selectedLeft.id === rightId);

            setConnections([...connections, { x1: selectedLeft.x, y1: selectedLeft.y, x2: position.x, y2: position.y, correct: isCorrect }]);
            setSelectedLeft(null);
        }
    };

    // Reset connections
    const resetConnections = () => {
        setConnections([]);
        setSelectedLeft(null);
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    // Fetch questions dynamically
    const fetchQuestions = () => {
        const usedKeys: string[] = [];
        const homeKeys = Object.keys(HomeItems);
        const shuffledKeys = shuffleArray([...homeKeys]);
        const selectedKeys = shuffledKeys.slice(0, 4); console.log(selectedKeys)
        const pairs = selectedKeys.map((key, index) => {
            const images = HomeItems[key];
            const randomImage = images[Math.floor(Math.random() * images.length)];
            usedKeys.push(randomImage);
        });
        const newQuestions: ImagePair[] = [];

        const suffledNumbers = shuffleArray([...usedKeys]); //console.log(suffledNumbers)
        selectedKeys.map((key, index) => {
            newQuestions.push({
                left_id: index + 1,
                right_id: usedKeys.indexOf(suffledNumbers[index]) + 1,
                leftImage: key,
                rightImage: suffledNumbers[index],
            });
        })

        //console.log(newQuestions)

        setQuestions(newQuestions);
    };

    useEffect(() => {
        resetConnections();
        fetchQuestions();
        document.title = "Match the Money – Currency Recognition Activity for UKG Kids | Mama’s Guidance"; // Set page title
    }, []);

    const nextQuestion = () => {
        resetConnections();
        setShowImage(true);
        setTimeout(() => {
            fetchQuestions();
            setShowImage(false);
        }, 2000);
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Fun UKG activity to match Indian coins and notes with their values. Build money identification skills through engaging worksheets and real-life connections." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/money/match-money" />
            </Helmet>
            {!showImage ? (
                <>
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>Match the Money</h1>

                            <p>
                                Money recognition is a key real-life skill every child should develop early. In this <strong>“Match the Money”</strong> activity, UKG students will match images of Indian coins and notes with their corresponding values. This fun exercise enhances their observation, number identification, and decision-making skills.
                            </p>

                            <h2>💰 What Will Children Learn?</h2>
                            <ul>
                                <li>Recognize Indian coins (₹1, ₹2, ₹5, ₹10)</li>
                                <li>Identify currency notes (₹10, ₹20, ₹50)</li>
                                <li>Understand the visual differences between coins and notes</li>
                                <li>Match money with numeric values</li>
                            </ul>

                            <h2>🧠 Skills Developed</h2>
                            <ul>
                                <li>Currency awareness</li>
                                <li>Visual matching and comparison</li>
                                <li>Number recognition</li>
                                <li>Basic money handling confidence</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/mathematics/smallest-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Smallest Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/mathematics/biggest-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Biggest Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/mathematics/number-comparison"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Number Comparison</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧩 At-Home Learning Ideas</h2>
                            <ul>
                                <li>Use real or toy money to play matching games</li>
                                <li>Play “mini shopping” games using coins and price tags</li>
                                <li>Let your child match wallet money to the value labels</li>
                            </ul>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>Match the Money</h1>

                        <p>
                            Money recognition is a key real-life skill every child should develop early. In this <strong>“Match the Money”</strong> activity, UKG students will match images of Indian coins and notes with their corresponding values. This fun exercise enhances their observation, number identification, and decision-making skills.
                        </p>

                        <h2>💰 What Will Children Learn?</h2>
                        <ul>
                            <li>Recognize Indian coins (₹1, ₹2, ₹5, ₹10)</li>
                            <li>Identify currency notes (₹10, ₹20, ₹50)</li>
                            <li>Understand the visual differences between coins and notes</li>
                            <li>Match money with numeric values</li>
                        </ul>

                        <h2>🧠 Skills Developed</h2>
                        <ul>
                            <li>Currency awareness</li>
                            <li>Visual matching and comparison</li>
                            <li>Number recognition</li>
                            <li>Basic money handling confidence</li>
                        </ul>


                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/ukg/mathematics/smallest-number"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Smallest Number</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/mathematics/biggest-number"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Biggest Number</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/mathematics/number-comparison"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Number Comparison</IonLabel>
                            </IonItem>
                        </div>

                        <h2>🧩 At-Home Learning Ideas</h2>
                        <ul>
                            <li>Use real or toy money to play matching games</li>
                            <li>Play “mini shopping” games using coins and price tags</li>
                            <li>Let your child match wallet money to the value labels</li>
                        </ul>
                    </article>
                    <div ref={containerRef} className="contentPart additionWord matchImage match-container" style={{ position: "relative", marginTop: "0%", paddingTop: "0%", height: "calc(-200px + 100vh)" }}>{/* SVG for drawing lines */}
                        <svg ref={svgRef} className="connector-svg" height={height} style={{ position: "absolute", top: 0, left: 0, width: "100%", pointerEvents: "none", zIndex: 10 }}>
                            {connections.map((conn, index) => (
                                <line
                                    key={index}
                                    x1={conn.x1}
                                    y1={conn.y1}
                                    x2={conn.x2}
                                    y2={conn.y2}
                                    stroke={conn.correct ? "green" : "red"} // Green if correct, Red if incorrect
                                    strokeWidth="3"
                                />
                            ))}
                        </svg>
                        <div className=" columns" style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 5, width: "100%", height: "100%" }}>
                            <div className="left-column">
                                {questions.map((item) => (
                                    <div
                                        key={item.left_id}
                                        className={`image-box ${selectedLeft?.id === item.left_id ? "selected" : ""}`}
                                        onClick={(e) => handleLeftClick(e, item.left_id)}
                                        style={{ cursor: "pointer", padding: "10px" }}
                                    >
                                        {item.leftImage}
                                    </div>
                                ))}
                            </div>
                            <div className="right-column">
                                {questions.map((item) => (
                                    <div key={item.left_id} className="image-box moneyTag" onClick={(e) => handleRightClick(e, item.right_id)} style={{ cursor: "pointer", padding: "10px" }}>
                                        <span className="emojiTag">{item.rightImage}</span><small style={{ fontSize: "60%", background: "#4255A4", color: "#FFF", padding: "10px" }}>{questions[item.right_id - 1].leftImage}</small>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="text-center">
                            <div className="btn hero-btn" onClick={nextQuestion}>Next Question</div>
                        </div>
                    </div>
                </>
            ) : (
                <div className="contentPart showMiddle">
                    <img style={{ height: "150px" }} className="img-fluid" src={process.env.REACT_APP_LOADING_LINK} />
                </div>
            )}
        </>
    );
};

export default MatchMoney;
